"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

interface UseNewFeatureBadgeOptions {
  /**
   * The key to use for localStorage
   */
  storageKey: string;
  /**
   * Optional path that triggers marking the feature as visited
   * When the current pathname matches this path, the badge will be hidden
   * and the visit will be persisted to localStorage
   */
  triggerPath?: string;
}

interface UseNewFeatureBadgeReturn {
  /**
   * Whether to show the "New" badge
   * Returns true if the user hasn't visited/seen the feature yet
   */
  showBadge: boolean;
  /**
   * Manually mark the feature as visited
   * This will hide the badge and persist the state to localStorage
   */
  markAsVisited: () => void;
}

/**
 * Hook to manage "New" feature badges with localStorage persistence
 *
 * @example
 * ```tsx
 * // Auto-mark as visited when user navigates to /portfolio
 * const { showBadge } = useNewFeatureBadge({
 *   storageKey: 'hasVisitedPortfolio',
 *   triggerPath: '/portfolio'
 * });
 *
 * <NavButton showNewBadge={showBadge}>Portfolio</NavButton>
 * ```
 *
 * @example
 * ```tsx
 * // Manually control when to mark as visited
 * const { showBadge, markAsVisited } = useNewFeatureBadge({
 *   storageKey: 'hasSeenFeature'
 * });
 *
 * <Dialog onOpenChange={(open) => open && markAsVisited()}>
 *   <Button showNewBadge={showBadge}>New Feature</Button>
 * </Dialog>
 * ```
 */
export const useNewFeatureBadge = ({
  storageKey,
  triggerPath,
}: UseNewFeatureBadgeOptions): UseNewFeatureBadgeReturn => {
  const pathname = usePathname();
  const [showBadge, setShowBadge] = useState(true);

  const markAsVisited = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, "true");
      setShowBadge(false);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Check if user has already visited
      const hasVisited = localStorage.getItem(storageKey) === "true";
      setShowBadge(!hasVisited);

      // Auto-mark as visited if on the trigger path
      if (triggerPath && pathname === triggerPath && !hasVisited) {
        markAsVisited();
      }
    }
  }, [pathname, storageKey, triggerPath]);

  return {
    showBadge,
    markAsVisited,
  };
};
